import type { AnimationEvent } from '@angular/animations';
import {
  animate,
  keyframes,
  style,
  transition,
  trigger,
} from '@angular/animations';
import type {
  ConnectedPosition,
  ConnectionPositionPair,
  FlexibleConnectedPositionStrategyOrigin,
  GlobalPositionStrategy,
  OverlayRef,
  PositionStrategy,
} from '@angular/cdk/overlay';
import {
  FlexibleConnectedPositionStrategy,
  Overlay,
  OverlayConfig,
  ScrollDispatcher,
} from '@angular/cdk/overlay';
import {
  CdkPortal,
  DomPortalOutlet,
  Portal,
  TemplatePortalDirective,
} from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { Timer, TimeUtils } from '@freelancer/time-utils';
import { FreelancerBreakpointValues } from '@freelancer/ui/breakpoints';
import { assertNever, toNumber } from '@freelancer/utils';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TESTING_ANIMATION_DISABLED } from '../ui.config';
import { CalloutColor } from './callout-color';
import {
  bottomCenter,
  bottomLeft,
  bottomRight,
  leftBottom,
  leftCenter,
  leftTop,
  rightBottom,
  rightCenter,
  rightTop,
  topCenter,
  topLeft,
  topRight,
} from './callout-connected-positions';
import { CalloutContentComponent } from './callout-content.component';
import { CalloutPlacement } from './callout-placement';
import { CalloutSize } from './callout-size';
import { CalloutTriggerComponent } from './callout-trigger.component';
import { CalloutService } from './callout.service';
import {
  CalloutComponentInterface,
  CalloutPosition,
  CalloutTriggerOpenInterface,
  CalloutViewportFit,
  FIXED_NAVIGATION_VIEWPORT_MARGIN,
} from './callout.types';

@Component({
  standalone: true,
  selector: 'fl-callout',
  imports: [
    NgTemplateOutlet,
    // eslint-disable-next-line deprecation/deprecation
    TemplatePortalDirective,
  ],
  template: `
    <ng-content select="fl-callout-trigger" />

    <ng-template cdk-portal>
      @if (!testingCloseAnimationDisabled) {
        @if (calloutIsOpen) {
          <div
            class="ContentContainer"
            [class.AngularAnimation]="!isMobileView() || !useSlideAnimation"
            [@paneAnimation]="getAnimationParams()"
            [@.disabled]="isMobileView() || !useSlideAnimation"
            [attr.data-mobile-fullscreen]="mobileFullscreen"
            [attr.data-placement]="placement"
            [class.mobile:hide]="hideMobile"
            (@paneAnimation.done)="animationDone($event)"
            (mouseenter)="handleContentContainerMouseenter()"
            (mouseleave)="handleContentContainerMouseleave()"
          >
            @if (!crawlable || calloutIsOpen) {
              <ng-container *ngTemplateOutlet="calloutContent" />
            }
          </div>
        }
      } @else {
        <div
          class="ContentContainer"
          [attr.data-mobile-fullscreen]="mobileFullscreen"
          [attr.data-placement]="placement"
          [class.mobile:hide]="hideMobile"
          (mouseenter)="handleContentContainerMouseenter()"
          (mouseleave)="handleContentContainerMouseleave()"
        >
          @if (!crawlable || calloutIsOpen) {
            <ng-container *ngTemplateOutlet="calloutContent" />
          }
        </div>
      }
    </ng-template>

    @if (crawlable && !calloutIsOpen) {
      <div [hidden]="true">
        <ng-container *ngTemplateOutlet="calloutContent" />
      </div>
    }

    <ng-template #calloutContent>
      <ng-content select="fl-callout-content" />
    </ng-template>
  `,
  styleUrls: ['./callout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('paneAnimation', [
      transition(
        ':enter',
        animate(
          '200ms ease-in-out',
          keyframes([
            style({ opacity: 0, transform: '{{transform}}', offset: 0 }),
            style({
              opacity: 1,
              transform: '{{bounceTransform}}',
              offset: 0.5,
            }),
            style({ opacity: 1, transform: 'none', offset: 1 }),
          ]),
        ),
      ),
      transition(':leave', [
        animate(
          '200ms ease-in-out',
          style({ opacity: 0, transform: '{{transform}}' }),
        ),
      ]),
    ]),
  ],
})
export class CalloutComponent
  implements CalloutComponentInterface, OnInit, OnChanges, OnDestroy
{
  private renderer = inject(Renderer2);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private overlay = inject(Overlay);
  private scrollDispatcher = inject(ScrollDispatcher);
  private timeUtils = inject(TimeUtils);
  private router = inject(Router);
  private calloutService = inject(CalloutService);
  private document = inject<Document>(DOCUMENT);
  readonly testingCloseAnimationDisabled? = inject(TESTING_ANIMATION_DISABLED, {
    optional: true,
  });

  @Input() color: CalloutColor = CalloutColor.LIGHT;
  @Input() bigBorderRadius = false; // We added this so that we match the radius of the containers in Messaging Redesign
  @Input() edgeToEdge = false;
  @Input() hideCloseButton = false;
  @Input() hover = false;
  @Input() placement = CalloutPlacement.BOTTOM;
  @Input() size?: CalloutSize;
  @Input() useSlideAnimation = false;
  @Input() mobileCloseButton = true;

  /**
   * Delay in milliseconds before the callout opens on hover, and before it
   * closes once the cursor leaves the trigger and content, so menus don't
   * flash open or shut as the cursor passes over them.
   * Only works when [hover]="true" otherwise will have no effect. Clicks, taps
   * and `open()` / `toggle()` always open immediately.
   * Set to 0 for no delay, which also leaves no time to move the cursor from
   * the trigger onto the content.
   */
  @Input() hoverDelay = 150;

  /* Allows use overflow visible if is need it */
  @Input() overflowContent = false;

  /**
   * Will embed the callout content in the DOM (but hidden), when the callout is close.
   * Use if you want the content to crawlable for SEO purposes.
   */
  @Input() crawlable = false;

  /**
   * Show a backdrop, only applicable in tablet view and bigger.
   * Backdrop is always displayed in mobile view unless the callout is hidden for mobile.
   */
  @Input() backdrop = false;

  /**
   * Skip the dimmed page overlay. The invisible backdrop still
   * intercepts taps so they don't hit the page behind the callout.
   * Applies whenever a backdrop is shown (mobile dialog, `mobileDropdown`, or `[backdrop]`).
   */
  @Input() transparentBackdrop = false;

  /** Use when content is scrollable for a better mobile experience. */
  @Input() mobileFullscreen = false;

  /**
   * Use when callout is hoverable and clicking on the trigger performs an action.
   * This allow mobile devices to trigger callout after pressing for 500ms.
   * It is important to note that callout will not be triggered on desktop with
   * mobile viewport as opening the dialog will break the flow of the trigger's
   * original action.
   */
  @Input() mobileLongPress = false;

  /**
   * Forces callout to open on tap/click. Useful when callout
   * trigger is a clickable element but is disabled. Use `mobileLongPress`
   * whenever possible.
   */
  @Input() mobileForceClick = false;

  /**
   * View header's title that shows up for mobileFullscreen
   */
  @Input() mobileHeaderTitle: string;

  /**
   * Keep the callout tethered to the trigger on mobile instead of opening
   * as a centered dialog. A dimmed backdrop still appears on mobile;
   * set `[transparentBackdrop]` to skip the dim.
   */
  @Input() mobileDropdown = false;

  @Input() hideMobile = false;
  /**
   * Used to set the offset of the callout from the trigger.
   * This is useful when the trigger is inline with other elements.
   */
  @Input() calloutOffsetInPixel = 4;

  /**
   * On by default; pass `{ enabled: false }` to opt out, or
   * `NAVIGATION_OWNED_VIEWPORT_FIT` for navigation-owned callouts.
   */
  @Input() viewportFit: CalloutViewportFit = { enabled: true };
  /**
   * Override the element the callout is anchored to for CDK positioning.
   * When not set the callout-trigger element is used as the anchor.
   */
  @Input() origin: FlexibleConnectedPositionStrategyOrigin | undefined =
    undefined;
  /** Prevents the callout from closing when clicking outside */
  @Input() preventCloseOnClick = false;
  /**
   * Re-position the callout whenever its overlay content changes size (via a
   * `ResizeObserver` on the overlay element).
   */
  @Input() repositionOnContentResize = true;
  /**
   * Renders the callout inside this element instead of the page overlay, so
   * the callout scrolls with the page. The element must be
   * `position: relative` and must not be inside an ancestor that clips
   * overflow. The callout keeps its primary placement and does not flip.
   * When a backdrop is shown, the callout uses the overlay.
   */
  @Input() inlineContainer?: HTMLElement;

  @Output() calloutClose = new EventEmitter<void>();
  @Output() calloutOpen = new EventEmitter<void>();

  @ContentChild(CalloutTriggerComponent, { static: true })
  triggerComponent: CalloutTriggerComponent;
  @ContentChild(CalloutContentComponent, { static: true })
  contentComponent: CalloutContentComponent;
  @ViewChild(CdkPortal, { static: true }) portal: Portal<any>;

  private triggerComponentTrigger?: Subscription;
  private triggerComponentOpen?: Subscription;
  private triggerComponentClose?: Subscription;
  @RepetitiveSubscription()
  private contentComponentClose?: Subscription;
  @RepetitiveSubscription()
  private contentComponentBodyClick?: Subscription;
  @RepetitiveSubscription()
  private contentComponentBodyMouseenter?: Subscription;
  @RepetitiveSubscription()
  private contentComponentBodyMouseleave?: Subscription;
  private routeChangeSubscription?: Subscription;
  @RepetitiveSubscription()
  private positionChangeSubscription?: Subscription;
  private resizeObserver?: ResizeObserver;

  private calloutNeedsToBeClosed = true;
  private documentClickEvent: () => void;
  private overlayRef?: OverlayRef;
  private inlineOutlet?: DomPortalOutlet;
  private removeInlineScrollListener?: () => void;
  private delayedOpenTimeoutId?: Timer;
  private delayedCloseTimeoutId?: Timer;

  private currentPlacement: CalloutPosition;
  private canRemoveOverflow = false;

  /**
   * Whether the callout has been opened by clicking.
   * Hover callouts can be forced to stay open by
   * clicking on the trigger
   */
  private isOpenedOnClick = false;
  isFullscreenMode = false;
  calloutIsOpen = false;

  // TODO: T267853 - The way we handle states of when a callout needs to be
  // closed or opened during a hover transition from the trigger
  // to the callout content is pretty all over the place. When
  // somebody has time (and clarity to think) redo the logic again.
  // Ref: T37940.

  ngOnInit(): void {
    if (this.hideMobile) {
      this.backdrop = false;
    }

    this.triggerComponent.setOptions({
      hover: this.hover,
      mobileForceClick: this.mobileForceClick,
      mobileLongPress: this.mobileLongPress,
      placement: this.placement,
    });

    this.documentClickEvent = this.renderer.listen('document', 'click', () => {
      if (this.calloutNeedsToBeClosed && !this.preventCloseOnClick) {
        this.closeCallout();
      }

      this.calloutNeedsToBeClosed = true;
    });

    this.triggerComponentTrigger = this.triggerComponent.trigger.subscribe(
      () => {
        this.toggleCallout();
      },
    );

    if (this.hover) {
      this.triggerComponentOpen = this.triggerComponent.open.subscribe(
        (event: CalloutTriggerOpenInterface) => {
          const isClickEvent = !!event?.isPinnedOnClick;

          if (this.calloutIsOpen) {
            // Click-opened: close on click, ignore hover
            if (this.isOpenedOnClick) {
              if (isClickEvent) {
                this.closeCallout();
              }
              return;
            }
            // If hover-opened and user clicks, pin it open
            if (isClickEvent) {
              this.isOpenedOnClick = true;
            } else {
              // Hover event while callout is already open - cancel any pending close
              this.calloutNeedsToBeClosed = false;
            }
          } else {
            // Open the callout
            this.calloutNeedsToBeClosed = false;
            this.isOpenedOnClick = isClickEvent;

            if (isClickEvent) {
              // Click: open immediately, bypass delay
              this.cancelDelayedOpen();
              this.openCallout();
            } else {
              // Hover: open after delay
              this.delayedOpenCallout();
            }
          }
        },
      );

      this.triggerComponentClose = this.triggerComponent.close.subscribe(() => {
        if (!this.isOpenedOnClick) {
          this.calloutNeedsToBeClosed = true;
          // Cancel any pending delayed open when mouse leaves the trigger
          this.cancelDelayedOpen();
          this.delayedCloseCallout();
        }
      });
    }

    this.routeChangeSubscription = this.router.events
      .pipe(
        filter(event => {
          // only close on page changes, and replacing the URL usually is not a full navigation
          const isReplaceUrl =
            this.router.getCurrentNavigation()?.extras?.replaceUrl ?? false;
          return event instanceof NavigationStart && !isReplaceUrl;
        }),
      )
      .subscribe(() => {
        this.close();
      });

    // default placement is center
    this.currentPlacement =
      this.placement === 'top' || this.placement === 'bottom'
        ? {
            overlayX: 'center',
            // flip it because the `currentPlacement` is for the overlay
            // ie. if the callout is on top of the trigger,
            // the overlay connection is at the bottom of the overlay
            overlayY: this.placement === 'top' ? 'bottom' : 'top',
            anchor: 'overlayY',
          }
        : {
            overlayX: this.placement === 'start' ? 'end' : 'start',
            overlayY: 'center',
            anchor: 'overlayX',
          };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (('hideMobile' in changes || 'backdrop' in changes) && this.hideMobile) {
      this.backdrop = false;
    }
  }

  // Allow animation to finish before disposing the content
  animationDone(event: AnimationEvent): void {
    // A callout reopened during its close animation must keep its new content
    if (event.toState === 'void' && !this.calloutIsOpen) {
      this.disposeContent();
    }
  }

  open(): void {
    if (!this.checkIfTriggerIsInView()) {
      this.triggerComponent.containerRef.element.nativeElement.scrollIntoView();
    }
    this.cancelDelayedOpen();
    if (!this.calloutIsOpen) {
      this.openCallout();
    }
  }

  close(): void {
    this.closeCallout();
  }

  toggle(): void {
    this.toggleCallout();
  }

  /**
   * Use this only if you move the callout trigger around
   */
  updatePosition(): void {
    if (this.inlineOutlet) {
      this.positionInline();
      return;
    }
    this.overlayRef?.updatePosition();
  }

  private checkIfTriggerIsInView(): boolean {
    const triggerBoundingRect = this.triggerComponent.getElementBoundingRect();
    return (
      triggerBoundingRect.top >= 0 &&
      triggerBoundingRect.left >= 0 &&
      triggerBoundingRect.right <=
        (window.innerWidth || document.documentElement.clientWidth) &&
      triggerBoundingRect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight)
    );
  }

  /** Open/close a callout immediately. */
  private toggleCallout(): void {
    if (this.calloutIsOpen) {
      this.closeCallout();
    } else {
      this.cancelDelayedOpen();
      this.openCallout();
    }
  }

  /** Opens immediately. */
  private openCallout(): void {
    if (this.hover) {
      this.calloutService.activateHover(this);
    }

    this.isFullscreenMode = this.isMobileView() && this.mobileFullscreen;

    this.calloutIsOpen = true;
    this.triggerComponent.calloutOpen = true;

    // Clears content left over from a close animation that is still running
    this.disposeContent();
    if (this.inlineContainer && !this.shouldShowBackdrop()) {
      this.attachInline(this.inlineContainer);
    } else {
      this.attachOverlay();
    }

    this.contentComponent.setOptions({
      bigBorderRadius: this.bigBorderRadius,
      color: this.color,
      edgeToEdge: this.edgeToEdge,
      hideCloseButton: this.hideCloseButton,
      hover: this.hover,
      mobileCloseButton: this.mobileCloseButton,
      mobileFullscreen: this.mobileFullscreen,
      mobileHeaderTitle: this.mobileHeaderTitle,
      overflowContent: this.overflowContent,
      size: this.size,
      triggerHtmlElement: this.triggerComponent.nativeElement,
    });
    this.contentComponent.setPlacement(
      this.currentPlacement,
      this.triggerComponent.containerRef.element.nativeElement,
    );

    this.setCalloutZIndex();

    this.calloutNeedsToBeClosed = false;
    this.contentComponentClose = this.contentComponent.close.subscribe(() =>
      this.closeCallout(),
    );
    this.contentComponentBodyClick = this.contentComponent.bodyClick.subscribe(
      () => {
        this.calloutNeedsToBeClosed = false;
      },
    );

    if (this.hover) {
      this.contentComponentBodyMouseenter =
        this.contentComponent.bodyMouseenter.subscribe(() => {
          if (!this.isOpenedOnClick) {
            this.calloutNeedsToBeClosed = false;
          }
        });
      this.contentComponentBodyMouseleave =
        this.contentComponent.bodyMouseleave.subscribe(() => {
          if (!this.isOpenedOnClick) {
            this.calloutNeedsToBeClosed = true;
            this.delayedCloseCallout();
          }
        });
    }

    this.togglePageScrollState(this.calloutIsOpen);
    this.calloutOpen.emit();
  }

  /**
   * Opens after `hoverDelay`, unless the cursor leaves the trigger before
   * then. Used for hover only.
   */
  private delayedOpenCallout(): void {
    if (this.calloutIsOpen) {
      return;
    }

    // Cancel any existing delayed open timeout
    this.cancelDelayedOpen();

    // T293469: Disable delayed open in the testing environment, as it causes
    // an animation listener binding error in the UI tests for the Find page.
    if (!this.hoverDelay || this.testingCloseAnimationDisabled) {
      this.openCallout();
    } else {
      this.delayedOpenTimeoutId = this.timeUtils.setTimeout(() => {
        if (!this.calloutNeedsToBeClosed) {
          this.openCallout();
        }
        this.delayedOpenTimeoutId = undefined;
      }, this.hoverDelay);
    }
  }

  private cancelDelayedOpen(): void {
    if (this.delayedOpenTimeoutId !== undefined) {
      clearTimeout(this.delayedOpenTimeoutId);
      this.delayedOpenTimeoutId = undefined;
    }
  }

  /**
   * Closes after `hoverDelay`, unless the cursor re-enters the trigger or
   * content before then.
   */
  private delayedCloseCallout(): void {
    // Restart the delay so an earlier leave can't close the callout early
    this.cancelDelayedClose();

    const delay = this.testingCloseAnimationDisabled ? 0 : this.hoverDelay;
    this.delayedCloseTimeoutId = this.timeUtils.setTimeout(() => {
      this.delayedCloseTimeoutId = undefined;
      if (this.calloutNeedsToBeClosed) {
        this.closeCallout();
      }
    }, delay);
  }

  private cancelDelayedClose(): void {
    if (this.delayedCloseTimeoutId !== undefined) {
      clearTimeout(this.delayedCloseTimeoutId);
      this.delayedCloseTimeoutId = undefined;
    }
  }

  /**
   * Handle mouseenter on the ContentContainer.
   * This cancels any pending close when the mouse enters the callout content.
   */
  handleContentContainerMouseenter(): void {
    if (this.hover && !this.isOpenedOnClick && !this.isMobileView()) {
      this.calloutNeedsToBeClosed = false;
    }
  }

  /**
   * Handle mouseleave on the ContentContainer.
   * This triggers close check when mouse fully leaves the callout area.
   */
  handleContentContainerMouseleave(): void {
    if (this.hover && !this.isOpenedOnClick && !this.isMobileView()) {
      this.calloutNeedsToBeClosed = true;
      this.delayedCloseCallout();
    }
  }

  /** Closes immediately. */
  private closeCallout(): void {
    if (this.calloutIsOpen) {
      this.detachOverlay();
      if (this.isMobileView() || this.testingCloseAnimationDisabled) {
        // Detach immediately if the animation flag is provided as the `animationDone`
        // callback timing is later than expected in in consecutive UI tests.
        this.disposeContent();
      }

      this.calloutIsOpen = false;
      this.triggerComponent.calloutOpen = false;
      this.calloutNeedsToBeClosed = true;
      if (this.contentComponentClose) {
        this.contentComponentClose.unsubscribe();
      }
      if (this.contentComponentBodyClick) {
        this.contentComponentBodyClick.unsubscribe();
      }

      if (this.hover) {
        if (this.contentComponentBodyMouseenter) {
          this.contentComponentBodyMouseenter.unsubscribe();
        }
        if (this.contentComponentBodyMouseleave) {
          this.contentComponentBodyMouseleave.unsubscribe();
        }
      }

      this.togglePageScrollState(this.calloutIsOpen);
      this.calloutClose.emit();
    }
  }

  private detachOverlay(): void {
    if (this.inlineOutlet?.hasAttached()) {
      // Inline content has no close animation, so remove its outlet now
      this.inlineOutlet.dispose();
      this.inlineOutlet = undefined;
      this.removeInlineScrollListener?.();
      this.removeInlineScrollListener = undefined;
    } else if (this.overlayRef?.hasAttached()) {
      // clean up zIndex hacking on the global callout parent
      // the `hostElement` itself is destroyed after detaching
      // so we don't need to worry about it
      if (this.overlayRef.hostElement.parentElement) {
        this.overlayRef.hostElement.parentElement.style.zIndex = '9001';
      }
      this.overlayRef.detach();
    } else {
      return;
    }

    if (this.positionChangeSubscription) {
      this.positionChangeSubscription.unsubscribe();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  /**
   * Calculates the effective z-index of the trigger component,
   * then sets the z-index of the content to be just above.
   */
  private setCalloutZIndex(): void {
    // don't do any z-index trickery for mobile dialogs or if backdrop is set
    // as both dialogs and fullscreen callouts should always be on top
    if (this.shouldShowBackdrop()) {
      return;
    }

    // loop through the parent elements until we find one with a defined z-index
    // it's a bit gross but there's no easy one-call function to get applied z-index
    let currElement: HTMLElement =
      this.triggerComponent.containerRef.element.nativeElement;
    let currZIndex: string | null = window.getComputedStyle(currElement).zIndex;
    let effectiveZIndex = toNumber(currZIndex) || 0;

    while (currElement.parentElement) {
      currElement = currElement.parentElement;
      currZIndex = window.getComputedStyle(currElement).zIndex;
      effectiveZIndex = Math.max(effectiveZIndex, toNumber(currZIndex) || 0);
    }

    this.contentComponent.zIndex = effectiveZIndex + 11;

    if (this.inlineOutlet) {
      this.renderer.setStyle(
        this.inlineOutlet.outletElement,
        'z-index',
        effectiveZIndex + 11,
      );
      return;
    }

    // set the parent zIndexes as well to override the Material styles
    // which by default would cause it to sit above nav/messaging/etc.
    if (this.overlayRef?.hostElement.parentElement) {
      this.overlayRef.hostElement.style.zIndex = `${effectiveZIndex + 11}`;
      this.overlayRef.hostElement.parentElement.style.zIndex = `${
        effectiveZIndex + 11
      }`;
      this.overlayRef.overlayElement.style.zIndex = `${effectiveZIndex + 11}`;
    }
  }

  /**
   * Prevents body from scrolling on mobile if
   * it becomes a dialog with overlay
   */
  togglePageScrollState(isCalloutOpen: boolean): void {
    const target = this.document.body;
    const currentOverflow = target.style.overflow;

    // We only want to remove overflow when callout component is the
    // one that set it. For instance, when callout is placed within a modal
    // overflow is already set and we don't want to remove it
    // once callout is closed
    if (isCalloutOpen && !currentOverflow) {
      this.canRemoveOverflow = true;
    }

    if (isCalloutOpen && this.isMobileDialog()) {
      this.renderer.setStyle(target, 'overflow', 'hidden');
    } else if (!isCalloutOpen && this.canRemoveOverflow) {
      this.renderer.removeStyle(target, 'overflow');
      this.canRemoveOverflow = false;
    }
  }

  // Need to remove private for D213925
  isMobileView(): boolean {
    return window.innerWidth < parseInt(FreelancerBreakpointValues.TABLET, 10);
  }

  /**
   * Whether this modal pops up on mobile as a dialog,
   * with a backdrop + not tethered to the element.
   *
   * Also includes fullscreen ones.
   */
  private isMobileDialog(): boolean {
    return this.isMobileView() && !this.hideMobile && !this.mobileDropdown;
  }

  private shouldShowBackdrop(): boolean {
    return (
      this.isMobileDialog() ||
      this.backdrop ||
      (this.isMobileView() && this.mobileDropdown)
    );
  }

  ngOnDestroy(): void {
    this.cancelDelayedOpen();
    this.cancelDelayedClose();
    this.closeCallout();
    this.detachOverlay();
    this.disposeContent();

    if (this.documentClickEvent) {
      this.documentClickEvent();
    }

    if (this.triggerComponentTrigger) {
      this.triggerComponentTrigger.unsubscribe();
    }

    if (this.hover) {
      if (this.triggerComponentOpen) {
        this.triggerComponentOpen.unsubscribe();
      }
      if (this.triggerComponentClose) {
        this.triggerComponentClose.unsubscribe();
      }
    }

    if (this.routeChangeSubscription) {
      this.routeChangeSubscription.unsubscribe();
    }

    if (this.positionChangeSubscription) {
      this.positionChangeSubscription.unsubscribe();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /**
   * Returns the connection offset for the callout.
   * We add a little bit of an offset to put a gap between the arrow and trigger.
   *
   * TODO: remove - I'm only adding this now to mantain legacy UI and make sure
   * that the screenshot tests pass properly.
   *
   * FIXME: not setting the offset causes the close button to be mispositioned
   * Even a `translate(0px)` somehow fixes it, but passing 0 does not set that,
   * so we have to have some offset gap.
   */
  private getOffset(): { offsetX: number; offsetY: number } {
    const anchor = this.currentPlacement[this.currentPlacement.anchor];

    return {
      offsetX:
        anchor === 'start'
          ? this.calloutOffsetInPixel
          : anchor === 'end'
            ? -this.calloutOffsetInPixel
            : 0,
      offsetY:
        anchor === 'top'
          ? this.calloutOffsetInPixel
          : anchor === 'bottom'
            ? -this.calloutOffsetInPixel
            : 0,
    };
  }

  private getPositions(): ConnectedPosition[] {
    const offset = this.getOffset();

    // `mobileDropdown` can flip the callout to the opposite side of the trigger,
    // so we need to handle that case that the callout is above the trigger when
    // it's on the bottom.

    const offsetY = (pos: ConnectedPosition): number => {
      if (!(this.isMobileView() && this.mobileDropdown)) {
        return offset.offsetY;
      }
      return pos.overlayY === 'bottom'
        ? -this.calloutOffsetInPixel
        : this.calloutOffsetInPixel;
    };

    const offsetX = (pos: ConnectedPosition): number => {
      if (!(this.isMobileView() && this.mobileDropdown)) {
        return offset.offsetX;
      }
      return pos.overlayX === 'end'
        ? -this.calloutOffsetInPixel
        : this.calloutOffsetInPixel;
    };

    const topPositions: ConnectedPosition[] = [
      { ...topCenter },
      { ...topLeft },
      { ...topRight },
      { ...bottomCenter },
      { ...bottomLeft },
      { ...bottomRight },
    ];

    const bottomPositions: ConnectedPosition[] = [
      { ...bottomCenter },
      { ...bottomLeft },
      { ...bottomRight },
      { ...topCenter },
      { ...topLeft },
      { ...topRight },
    ];

    const leftPositions: ConnectedPosition[] = [
      { ...leftCenter },
      { ...leftBottom },
      { ...leftTop },
    ];

    const rightPositions: ConnectedPosition[] = [
      { ...rightCenter },
      { ...rightBottom },
      { ...rightTop },
    ];

    const rightBottomPositions: ConnectedPosition[] = [
      { ...rightBottom },
      { ...rightTop },
    ];

    const rightTopPositions: ConnectedPosition[] = [
      { ...rightTop },
      { ...rightBottom },
    ];

    const bottomLeftPositions: ConnectedPosition[] = [
      { ...bottomLeft },
      { ...topLeft },
    ];

    const bottomRightPositions: ConnectedPosition[] = [
      { ...bottomRight },
      { ...topRight },
    ];

    switch (this.placement) {
      case CalloutPlacement.TOP:
        return [...topPositions, ...bottomPositions].map(pos => ({
          ...pos,
          offsetY: offsetY(pos),
        }));
      case CalloutPlacement.BOTTOM:
        return [...bottomPositions, ...topPositions].map(pos => ({
          ...pos,
          offsetY: offsetY(pos),
        }));
      case CalloutPlacement.LEFT:
        return [...leftPositions, ...rightPositions].map(pos => ({
          ...pos,
          offsetX: offsetX(pos),
        }));
      case CalloutPlacement.RIGHT:
        return [...rightPositions, ...leftPositions].map(pos => ({
          ...pos,
          offsetX: offsetX(pos),
        }));
      case CalloutPlacement.BOTTOM_LEFT:
        return [...bottomLeftPositions, ...topPositions].map(pos => ({
          ...pos,
          offsetY: offsetY(pos),
        }));
      case CalloutPlacement.BOTTOM_RIGHT:
        return [...bottomRightPositions, ...topPositions].map(pos => ({
          ...pos,
          offsetY: offsetY(pos),
        }));
      case CalloutPlacement.RIGHT_BOTTOM:
        return [...rightBottomPositions, ...leftPositions].map(pos => ({
          ...pos,
          offsetX: offsetX(pos),
        }));
      case CalloutPlacement.RIGHT_TOP:
        return [...rightTopPositions, ...leftPositions].map(pos => ({
          ...pos,
          offsetX: offsetX(pos),
        }));
      default:
        assertNever(this.placement);
    }
  }

  private updateCalloutArrowPosition(
    connectionPair: ConnectionPositionPair,
  ): void {
    const prevAnchorPosition = this.currentPlacement.anchor;
    this.currentPlacement = { ...connectionPair, anchor: prevAnchorPosition };
    this.contentComponent.setPlacement(
      this.currentPlacement,
      this.triggerComponent.containerRef.element.nativeElement,
    );
    this.changeDetectorRef.markForCheck();
  }

  getSize(): number | undefined {
    switch (this.size) {
      case CalloutSize.MEDIUM:
        return 400;
      case CalloutSize.SMALL:
        return 270;
      default:
        // default: undefined (determined by size of content)
        return undefined;
    }
  }

  private attachOverlay(): void {
    this.overlayRef = this.createOverlay();
    this.overlayRef.attach(this.portal);

    if (this.repositionOnContentResize) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.overlayRef?.hasAttached()) {
          this.updatePosition();
        }
      });
      this.resizeObserver.observe(this.overlayRef.overlayElement);
    }
  }

  private attachInline(container: HTMLElement): void {
    const [position] = this.getPositions();
    this.currentPlacement = { ...position, anchor: this.getAnchor() };

    const size = this.getSize();
    const element = this.renderer.createElement('div');
    this.renderer.setStyle(element, 'position', 'absolute');
    this.renderer.setStyle(
      element,
      'width',
      size === undefined ? 'max-content' : `${size}px`,
    );
    this.renderer.appendChild(container, element);

    this.inlineOutlet = new DomPortalOutlet(element);
    this.inlineOutlet.attach(this.portal);
    this.positionInline();

    // The percentage transform handles content resizes
    this.resizeObserver = new ResizeObserver(() => this.positionInline());
    this.resizeObserver.observe(container);
    this.resizeObserver.observe(this.triggerComponent.nativeElement);
    // A page scroll moves the trigger and the callout together, so only a
    // scrolling element between the container and the trigger needs an
    // update. Scroll events do not bubble, so listen in the capture phase.
    this.removeInlineScrollListener = this.renderer.listen(
      container,
      'scroll',
      () => this.positionInline(),
      { capture: true, passive: true },
    );
  }

  // Moves the inline callout to its placement point on the trigger
  private positionInline(): void {
    const container = this.inlineOutlet?.outletElement.parentElement;
    if (!this.inlineOutlet || !container) {
      return;
    }

    const fraction = (point: string): number =>
      point === 'center' ? 0.5 : point === 'end' || point === 'bottom' ? 1 : 0;
    const [{ originX, originY, overlayX, overlayY, offsetX, offsetY }] =
      this.getPositions();
    const triggerRect = this.triggerComponent.getElementBoundingRect();
    const containerRect = container.getBoundingClientRect();

    // The outlet is positioned from the padding box of the container
    const left =
      triggerRect.left -
      containerRect.left -
      container.clientLeft +
      container.scrollLeft +
      triggerRect.width * fraction(originX) +
      (offsetX ?? 0);
    const top =
      triggerRect.top -
      containerRect.top -
      container.clientTop +
      container.scrollTop +
      triggerRect.height * fraction(originY) +
      (offsetY ?? 0);
    const { outletElement } = this.inlineOutlet;

    this.renderer.setStyle(outletElement, 'left', `${left}px`);
    this.renderer.setStyle(outletElement, 'top', `${top}px`);
    this.renderer.setStyle(
      outletElement,
      'transform',
      `translate(${-fraction(overlayX) * 100}%, ${-fraction(overlayY) * 100}%)`,
    );
  }

  private getAnchor(): CalloutPosition['anchor'] {
    return ['top', 'bottom', 'bottom-left', 'bottom-right'].includes(
      this.placement,
    )
      ? 'overlayY'
      : 'overlayX';
  }

  private disposeContent(): void {
    this.inlineOutlet?.dispose();
    this.inlineOutlet = undefined;
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }

  private createOverlay(): OverlayRef {
    const positions = this.getPositions();
    this.currentPlacement = { ...positions[1], anchor: this.getAnchor() };

    // Reposition callout when scrolled
    const scrollStrategy = this.overlay.scrollStrategies.reposition({
      scrollThrottle: 5,
    });

    const positionAnchor =
      this.origin ?? this.triggerComponent.containerRef.element;

    const fitViewport =
      this.viewportFit.enabled || (this.isMobileView() && this.mobileDropdown);

    let positionStrategy: PositionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(positionAnchor)
      .withPositions(positions)
      .withPush(fitViewport)
      .withFlexibleDimensions(fitViewport)
      .withViewportMargin(
        fitViewport && !this.isMobileView()
          ? (this.viewportFit.options?.viewportMargin ??
              (this.placement === CalloutPlacement.TOP
                ? FIXED_NAVIGATION_VIEWPORT_MARGIN
                : 0))
          : 0,
      )
      .withScrollableContainers(
        this.scrollDispatcher.getAncestorScrollContainers(
          this.triggerComponent.containerRef.element,
        ),
      );

    if (this.isMobileDialog()) {
      positionStrategy = this.overlay.position().global();
      if (this.mobileFullscreen) {
        positionStrategy = (positionStrategy as GlobalPositionStrategy)
          .left('0')
          .top('0');
      } else {
        positionStrategy = (positionStrategy as GlobalPositionStrategy)
          .centerHorizontally()
          .centerVertically();
      }
    }

    const width = this.isMobileView()
      ? this.mobileFullscreen
        ? // mobile and mobileFullscreen: fullscreen width
          '100%'
        : // mobile but not mobileFullscreen: dialog filling most of the screen
          this.mobileDropdown
          ? this.getSize()
          : '90%'
      : // non-mobile: set size if CalloutSize is set
        this.getSize();

    const height =
      this.isMobileView() && this.mobileFullscreen ? '100%' : undefined;

    const state = new OverlayConfig({
      hasBackdrop: this.shouldShowBackdrop(),
      backdropClass: this.transparentBackdrop
        ? 'cdk-overlay-transparent-backdrop'
        : undefined,
      height,
      width,
      positionStrategy,
      scrollStrategy,
    });

    if (positionStrategy instanceof FlexibleConnectedPositionStrategy) {
      this.positionChangeSubscription =
        positionStrategy.positionChanges.subscribe(change => {
          this.updateCalloutArrowPosition(change.connectionPair);
        });
    }

    return this.overlay.create(state);
  }

  getAnimationParams(): {
    value: string;
    params: { transform: string; bounceTransform: string };
  } {
    switch (this.placement) {
      case 'top':
        return {
          value: '',
          params: {
            transform: 'translateY(-10px)',
            bounceTransform: 'translateY(10px)',
          },
        };
      case 'bottom':
      case 'bottom-left':
      case 'bottom-right':
        return {
          value: '',
          params: {
            transform: 'translateY(10px)',
            bounceTransform: 'translateY(-10px)',
          },
        };
      case 'start':
        return {
          value: '',
          params: {
            transform: 'translateX(-10px)',
            bounceTransform: 'translateX(10px)',
          },
        };
      case 'end':
      case 'end-bottom':
      case 'end-top':
        return {
          value: '',
          params: {
            transform: 'translateX(10px)',
            bounceTransform: 'translateX(-10px)',
          },
        };
      default:
        return {
          value: '',
          params: {
            transform: 'translateY(10px)',
            bounceTransform: 'translateY(-10px)',
          },
        };
    }
  }
}
